// src/components/settings/Backups.tsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { backupAPI, BackupStatus, BackupLogEntry } from '../../services/api';
import { useGlobalToast } from '../../App';
import { useDragScroll } from '../../hooks/useDragScroll';
import { AccountActionButton } from '../common/Buttons';
import {
  SettingsBlock,
  SettingsCard,
  Section,
  SectionLabel,
  SettingDescription,
  RetentionGrid,
  RetentionStat,
  StatLabel,
  StatValue,
  formatDateTime,
} from './shared/SystemSettingsElements';
import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableBody,
  StyledTableRow,
  StyledTableCell,
  StyledTableActionsCell,
} from './shared/SettingsTableElements';

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
};

const Backups: React.FC = () => {
  const { showSuccess, showError } = useGlobalToast();
  const theme = useTheme();
  const [status, setStatus] = useState<BackupStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const dragScrollRef = useDragScroll<HTMLDivElement>();

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      const result = await backupAPI.getStatus();
      setStatus(result);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleRunNow = async () => {
    try {
      setRunning(true);
      const result = await backupAPI.runNow();
      if (result.success) {
        showSuccess(`Backup complete (${formatBytes(result.sizeBytes)})`);
      } else {
        showError(result.errorMessage || 'Backup failed');
      }
      await loadStatus();
    } catch {
      showError('Failed to run backup');
    } finally {
      setRunning(false);
    }
  };

  const handleDownload = async (entry: BackupLogEntry) => {
    try {
      const blob = await backupAPI.download(entry.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = entry.fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showError('Failed to download backup');
    }
  };

  return (
    <SettingsBlock>
      <SettingsCard elevation={0}>
        <Section>
          <SectionLabel>Database Backups</SectionLabel>
          <SettingDescription sx={{ mb: 1 }}>
            Manual and scheduled MySQL backups, stored on the server and
            available for download below.
          </SettingDescription>

          {loading && !status ? (
            <Box sx={{ py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : status ? (
            <>
              <RetentionGrid>
                <RetentionStat>
                  <StatLabel>Auto-backup</StatLabel>
                  <StatValue>
                    {status.enabled ? 'Enabled' : 'Disabled'}
                  </StatValue>
                </RetentionStat>
                <RetentionStat>
                  <StatLabel>Every</StatLabel>
                  <StatValue>{status.intervalHours} hours</StatValue>
                </RetentionStat>
                <RetentionStat>
                  <StatLabel>Kept</StatLabel>
                  <StatValue>{status.retentionCount} backups</StatValue>
                </RetentionStat>
                <RetentionStat>
                  <StatLabel>Last backup</StatLabel>
                  <StatValue>{formatDateTime(status.lastBackupAt)}</StatValue>
                </RetentionStat>
                <RetentionStat>
                  <StatLabel>Next backup</StatLabel>
                  <StatValue>{formatDateTime(status.nextBackupAt)}</StatValue>
                </RetentionStat>
              </RetentionGrid>

              <Box
                sx={{
                  mt: 1.5,
                  display: 'flex',
                  gap: 1,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <AccountActionButton
                  variant="contained"
                  onClick={handleRunNow}
                  disabled={running}
                  bgColor={theme.palette.primary.main}
                  textColor={theme.palette.grey[0] || '#fff'}
                  hoverBgColor={theme.palette.primary.dark}
                  hoverTextColor="#fff"
                  minWidth="292px"
                  startIcon={
                    running ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {running ? 'Running...' : 'Run backup now'}
                </AccountActionButton>
                <AccountActionButton
                  variant="outlined"
                  onClick={loadStatus}
                  disabled={loading || running}
                  minWidth="156px"
                >
                  Refresh
                </AccountActionButton>
              </Box>

              {status.recent.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <StyledTableContainer ref={dragScrollRef}>
                    <Table size="small">
                      <StyledTableHead>
                        <StyledTableRow>
                          <StyledTableCell>File</StyledTableCell>
                          <StyledTableCell>Date</StyledTableCell>
                          <StyledTableCell>Size</StyledTableCell>
                          <StyledTableCell>Duration</StyledTableCell>
                          <StyledTableCell>Triggered</StyledTableCell>
                          <StyledTableActionsCell>
                            Action
                          </StyledTableActionsCell>
                        </StyledTableRow>
                      </StyledTableHead>
                      <StyledTableBody>
                        {status.recent.map((entry, index) => (
                          <StyledTableRow
                            key={entry.id}
                            isOdd={index % 2 === 1}
                          >
                            <StyledTableCell>
                              {entry.success ? (
                                entry.fileName
                              ) : (
                                <Tooltip
                                  title={entry.errorMessage || 'Backup failed'}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      color: 'error.main',
                                    }}
                                  >
                                    <ErrorIcon fontSize="small" />
                                    Failed
                                  </Box>
                                </Tooltip>
                              )}
                            </StyledTableCell>
                            <StyledTableCell>
                              {formatDateTime(entry.createdAt)}
                            </StyledTableCell>
                            <StyledTableCell>
                              {entry.success
                                ? formatBytes(entry.sizeBytes)
                                : '—'}
                            </StyledTableCell>
                            <StyledTableCell>
                              {formatDuration(entry.durationMs)}
                            </StyledTableCell>
                            <StyledTableCell>
                              {entry.triggeredBy}
                            </StyledTableCell>
                            <StyledTableActionsCell>
                              {entry.success && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownload(entry)}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              )}
                            </StyledTableActionsCell>
                          </StyledTableRow>
                        ))}
                      </StyledTableBody>
                    </Table>
                  </StyledTableContainer>
                </Box>
              )}
            </>
          ) : (
            <SettingDescription>Status unavailable</SettingDescription>
          )}
        </Section>
      </SettingsCard>
    </SettingsBlock>
  );
};

export default Backups;
